import { format, parse } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { enAU } from 'date-fns/locale';
import { formatTime } from '~/helpers/formatDate';

type DateField =
  | 'scheduledStart'
  | 'scheduledEnd'
  | 'completedAt'
  | 'createdAt';

// Full job shape used for helpers that need timezone and all date fields
type Job = {
  timeZone: string;
  scheduleStart?: any;
  scheduleEnd?: any;
  completedAt?: any;
  createdAt?: any;
  scheduledStartTime?: any;
  scheduledEndTime?: any;
  scheduledStartDate?: any;
  scheduledEndDate?: any;
};

// Minimal shape needed for deriving a YYYY-MM-DD grouping key
type JobDateFields = {
  scheduleStart?: any;
  scheduleEnd?: any;
  scheduledStartDate?: any;
  scheduledEndDate?: any;
};

/**
 * Formats a job date from scheduledStartDate/scheduledStartTime fields
 * using the job's timezone. These fields are property-local strings that
 * should be interpreted in the property's timezone context.
 *
 * @param job - Job object with timeZone and date/time fields
 * @param field - Which date field to format ('scheduledStart' or 'scheduledEnd')
 * @param pattern - date-fns pattern (default: 'PP')
 * @returns Formatted date string, or null if date is missing
 */
export function formatJobDateFromParts(
  job: Job,
  field: 'scheduledStart' | 'scheduledEnd',
  pattern: string = 'PP'
): string | null {
  const date =
    field === 'scheduledStart' ? job.scheduledStartDate : job.scheduledEndDate;
  const time =
    field === 'scheduledStart' ? job.scheduledStartTime : job.scheduledEndTime;
  const timeZone = job.timeZone;

  if (!date || typeof date !== 'string' || !date.trim()) {
    return null;
  }

  if (!timeZone) {
    // Fallback: parse without timezone (shouldn't happen in practice)
    try {
      const parsedDate = parse(date.trim(), 'yyyy-MM-dd', new Date());
      if (isNaN(parsedDate.getTime())) {
        return null;
      }
      const datePart = format(parsedDate, pattern, { locale: enAU });
      if (time && typeof time === 'string' && time.trim()) {
        const timePart = formatTime(time);
        return timePart ? `${datePart} ${timePart}` : datePart;
      }
      return datePart;
    } catch (error) {
      console.warn('Invalid date format:', date, error);
      return null;
    }
  }

  try {
    // Parse date and time as property-local, then convert to UTC using property timezone
    const safeTime =
      (time && typeof time === 'string' && time.trim()) || '00:00';
    const isoLocal = `${date.trim()}T${safeTime}:00`;

    // Convert property-local datetime to UTC using property timezone
    const utcDate = zonedTimeToUtc(isoLocal, timeZone);
    if (isNaN(utcDate.getTime())) {
      return null;
    }

    // Format in the property's timezone
    const datePart = formatInTimeZone(utcDate, timeZone, pattern, {
      locale: enAU,
    });

    // If time was provided, append formatted time
    if (time && typeof time === 'string' && time.trim()) {
      const timePart = formatTime(time);
      return timePart ? `${datePart} ${timePart}` : datePart;
    }

    return datePart;
  } catch (error) {
    console.warn('Invalid date/timezone format:', date, timeZone, error);
    return null;
  }
}

/**
 * Gets a YYYY-MM-DD string for grouping/sorting from job date fields.
 * Prefers scheduledStartDate/scheduledEndDate if available, otherwise
 * extracts from scheduleStart/scheduleEnd ISO strings.
 *
 * @param job - Job object with date fields
 * @param field - Which date field to extract ('scheduledStart' or 'scheduledEnd')
 * @returns YYYY-MM-DD string or null
 */
export function getJobDateKey(
  job: JobDateFields,
  field: 'scheduledStart' | 'scheduledEnd'
): string | null {
  if (field === 'scheduledStart') {
    // Prefer scheduledStartDate if available (property-local, no timezone conversion needed)
    if (job.scheduledStartDate && typeof job.scheduledStartDate === 'string') {
      return job.scheduledStartDate.trim();
    }
    // Fallback to extracting date from scheduleStart ISO string
    if (job.scheduleStart && typeof job.scheduleStart === 'string') {
      return job.scheduleStart.slice(0, 10); // Extract YYYY-MM-DD from ISO string
    }
  } else {
    // Prefer scheduledEndDate if available
    if (job.scheduledEndDate && typeof job.scheduledEndDate === 'string') {
      return job.scheduledEndDate.trim();
    }
    // Fallback to extracting date from scheduleEnd ISO string
    if (job.scheduleEnd && typeof job.scheduleEnd === 'string') {
      return job.scheduleEnd.slice(0, 10);
    }
  }
  return null;
}

// Full date
const DATE_FIELD_MAP = {
  scheduledStart: 'scheduleStart',
  scheduledEnd: 'scheduleEnd',
  completedAt: 'completedAt',
  createdAt: 'createdAt',
} as const;

// local time
const TIME_FIELD_MAP = {
  scheduledStart: 'scheduledStartTime',
  scheduledEnd: 'scheduledEndTime',
  completedAt: null,
  createdAt: null,
} as const;

export function formatLocalDate(job: Job, field: DateField, pattern?: string) {
  const date = DATE_FIELD_MAP[field];

  if (!job[date]) {
    return null;
  }

  const time = TIME_FIELD_MAP[field];
  const d = new Date(job[date]);
  const tz = job.timeZone;
  const options = { locale: enAU };

  if (pattern) {
    return formatInTimeZone(d, tz, pattern, options);
  }

  // If this field has a separate local time string (e.g., scheduledStartTime),
  // prefer rendering the time from that value to avoid midnight defaults from scheduleStart.
  // Treat any explicit time value (including "00:00") as legitimate and display it.
  if (time !== null && job[time]) {
    const datePart = formatInTimeZone(d, tz, 'PP', options);
    const timePart = formatTime(job[time]);
    return timePart ? `${datePart} ${timePart}` : datePart;
  }

  // For fields without separate local time (e.g., createdAt/completedAt), include the time from the DateTime
  // Otherwise, render just the date when no explicit local time is provided
  return time === null
    ? formatInTimeZone(d, tz, 'PP p', options)
    : formatInTimeZone(d, tz, 'PP', options);
}

export function formatLocalTime(job: Job, field: DateField) {
  const date = DATE_FIELD_MAP[field];

  if (!job[date]) {
    return null;
  }

  const time = TIME_FIELD_MAP[field];
  const d = new Date(job[date]);
  const tz = job.timeZone;
  const options = { locale: enAU };

  // If there is a separate local time string for this field, render from it directly
  // Treat any explicit time value (including "00:00") as legitimate and display it.
  if (time !== null) {
    if (!job[time]) return null;
    return formatTime(job[time]);
  }

  // Otherwise (e.g., completedAt/createdAt), render the time from the DateTime value
  return formatInTimeZone(d, tz, 'p', options);
}
